import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Scale, Info, ShieldCheck, HeartHandshake, Copyright, Link, AlertTriangle, RefreshCw, Gavel } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/terms-conditions`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageTitle = "Terms & Conditions | SEVAA Foundation";
const pageDescription =
  "Read SEVAA Foundation's terms and conditions for website use, donations, intellectual property, external links, liability and governing law.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: pageTitle,
  url: pageUrl,
  inLanguage: "en-IN",
  description: pageDescription,
  isAccessibleForFree: true,
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
  },
  about: [
    "Terms and Conditions",
    "Website Use",
    "Donations",
    "Intellectual Property",
    "Governing Law",
    "Liability"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Terms & Conditions",
      item: pageUrl,
    },
  ],
};


const TermsConditions = () => {
  // Data array for the terms to keep the code clean
  const termsData = [
    {
      id: 1,
      title: "About Us",
      icon: <Info className="w-6 h-6 text-teal-600" />,
      content: "SEVAA (Swadesh Sevaa Charitable Foundation) is a nonprofit organization working for education, healthcare, food support, and social upliftment of poor, extremely poor, and tribal communities in India."
    },
    {
      id: 2,
      title: "Use of Website",
      icon: <ShieldCheck className="w-6 h-6 text-teal-600" />,
      content: "You agree to use this website only for lawful purposes and in a manner that does not violate any applicable laws or regulations. You must not:",
      list: [
        "Misuse or attempt to damage the website",
        "Attempt unauthorized access to data or systems",
        "Use the website for fraudulent or harmful activities"
      ]
    },
    {
      id: 3,
      title: "Donations",
      icon: <HeartHandshake className="w-6 h-6 text-teal-600" />,
      content: "All donations made through the website are voluntary contributions toward our charitable work. By donating, you confirm that:",
      list: [
        "The funds are your own and legally obtained",
        "You are authorized to make the payment",
        "You understand the donation will be used for social and humanitarian purposes"
      ]
    },
    {
      id: 4,
      title: "Intellectual Property",
      icon: <Copyright className="w-6 h-6 text-teal-600" />,
      content: "All content on this website, including text, graphics, logos, and materials, belongs to SEVAA unless otherwise stated. Unauthorized reproduction, copying, or distribution is prohibited."
    },
    {
      id: 5,
      title: "External Links",
      icon: <Link className="w-6 h-6 text-teal-600" />,
      content: "Our website may contain links to third-party websites. SEVAA is not responsible for the content, privacy practices, or policies of those external sites."
    },
    {
      id: 6,
      title: "Limitation of Liability",
      icon: <AlertTriangle className="w-6 h-6 text-teal-600" />,
      content: "SEVAA shall not be liable for any direct, indirect, or incidental damages resulting from the use of this website or reliance on its information."
    },
    {
      id: 7,
      title: "Modifications",
      icon: <RefreshCw className="w-6 h-6 text-teal-600" />,
      content: "We reserve the right to update or change these Terms at any time without prior notice. Continued use of the website implies acceptance of updated terms."
    },
    {
      id: 8,
      title: "Governing Law",
      icon: <Gavel className="w-6 h-6 text-teal-600" />,
      content: "These terms shall be governed by the laws of India. Any disputes shall fall under the jurisdiction of courts located in Noida, Uttar Pradesh."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation terms and conditions" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation terms and conditions" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

    <div className="bg-slate-50 min-h-screen font-poppins pb-20">
      
      {/* 1. HERO SECTION */}
      <div className="relative w-full h-[400px] bg-teal-900 overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute top-0 -right-40 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute -bottom-40 left-20 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-4xl mx-auto px-6 flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-teal-800 rounded-2xl border border-teal-700 flex items-center justify-center mb-6 shadow-xl"
          >
            <Scale className="w-8 h-8 text-orange-400" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
          >
            Terms & Conditions
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-teal-100 text-base md:text-lg max-w-2xl font-medium"
          >
            Welcome to the website of SEVAA. By accessing or using this website, you agree to comply with and be bound by the following terms.
          </motion.p>
        </div>
      </div>

      {/* 2. CONTENT SECTION */}
      <div className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-8 md:p-12">
          
          <p className="text-slate-500 mb-10 text-sm font-medium border-b border-slate-100 pb-6">
            If you do not agree with any part of these terms and conditions, please do not use our website.
          </p>

          <div className="space-y-12">
            {termsData.map((term, index) => (
              <motion.div 
                key={term.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex flex-col md:flex-row gap-4 md:gap-6 group"
              >
                {/* Icon Column */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors duration-300">
                    {React.cloneElement(term.icon, { className: "w-6 h-6 text-teal-600 group-hover:text-white transition-colors duration-300" })}
                  </div>
                </div>

                {/* Text Column */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="text-slate-300 font-black text-lg">{term.id}.</span> 
                    {term.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed font-medium">
                    {term.content}
                  </p>
                  
                  {/* Render list if it exists */}
                  {term.list && (
                    <ul className="mt-4 space-y-2">
                      {term.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2.5 flex-shrink-0"></span>
                          <span className="text-slate-600 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>

      </div>
    </>
  );
};

export default TermsConditions;
